// @flow
import React from "react";
import {
    Typography,
    MenuItem,
    withStyles,
    Button,
    Box,
} from "@material-ui/core";
import { Field, reduxForm } from "redux-form";
import { compose } from "redux";
import { connect } from "react-redux";
import WarningIcon from "@material-ui/icons/Warning";
import validate from "../utils/validate";
import RFInputField from "../components/RFInputField";
import RFInputFieldProgress from "../components/RFInputFieldProgress";
import RFCheckboxGroup from "../components/RFCheckboxGroup";
import RFUploadField from "../components/RFUploadField";
import RFCoordinateField from "../components/RFCoordinateField";
import RFDepartmentField from "../components/RFDepartmentField";
import RFRegionField from "../components/RFRegionField";
import RFPriceField from "../components/RFPriceField";
import type { NatureType } from "../../../core/entities/natureType.type";
import type { Region } from "../../../core/entities/region.type";
import styles from "../styles/styles";

type Props = {
    handleSubmit: Function,
    regions: Array<Region>,
    natureTypes: Array<?NatureType>,
    classes: Object,
    created: string,
    reset: Function,
    valid: boolean,
};

const FirstPage = (props: Props) => {
    const {
        handleSubmit,
        regions,
        natureTypes,
        classes,
        created,
        reset,
        valid,
    } = props;

    if (created) {
        reset("event");
    }

    const dates = [
        { value: "Vendredi 27", label: "Vendredi 27 Juin" },
        { value: "Samedi 28", label: "Samedi 28 Juin" },
        { value: "Dimanche 29", label: "Dimanche 29 Juin" },
    ];

    return (
        <>
            <form onSubmit={handleSubmit} id="form_page_1">
                <Typography variant=" " className="main__title">
                    ANIMATION
                </Typography>
                <Typography variant="body1" className="indication">
                    {
                        "Attention, toutes les informations saisies à propos de l'animation apparaitront telles quelles dans la programmation en ligne."
                    }
                    <br />
                    {
                        "Important : si vous avez différents lieux d'accueil du public et différentes animations, remplissez un formulaire pour chaque lieu et par animation."
                    }
                    <br />
                    {"Les champs suivis d'un astérisque * sont obligatoires."}
                </Typography>
                <Typography variant="h3" className="main__title">
                    A propos de l’animation
                </Typography>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        {"Région et département *"}
                    </Typography>
                    <Field
                        name="region"
                        component={RFRegionField}
                        id="region"
                        type="text"
                        displayEmpty
                    >
                        <MenuItem value="" disabled>
                            Régions *
                        </MenuItem>
                        {regions.map(region => (
                            <MenuItem key={region.id} value={region["@id"]}>
                                {region.name}
                            </MenuItem>
                        ))}
                    </Field>
                    <Field
                        name="department"
                        component={RFDepartmentField}
                        id="department"
                        type="text"
                    />
                </Box>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        {
                            "Coordonnées GPS (au format décimal, exemple : Latitude 48.9070592 Longitude 2.3150592) : "
                        }
                    </Typography>
                    <br />
                    <Typography variant="caption">
                        {"Trouvez vos coordonnées GPS sur "}
                        <a
                            href="http://www.coordonnees-gps.fr/"
                            title=""
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            http://www.coordonnees-gps.fr/
                        </a>
                        {" en indiquant le lieu de l'animation."}
                    </Typography>
                    <Field
                        name="lat"
                        component={RFCoordinateField}
                        label="Latitude"
                        type="text"
                        className="form__input"
                        required
                    />
                    <Field
                        name="lng"
                        component={RFCoordinateField}
                        label="Longitude"
                        type="text"
                        className="form__input"
                        required
                    />
                </Box>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        {"Nature de l'animation *"}
                    </Typography>
                    <Field
                        name="natureTypes"
                        component={RFCheckboxGroup}
                        className="form__checkbox"
                        options={natureTypes}
                        label="Nature de l'animation"
                    />
                </Box>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        {"Titre de l'animation"}
                    </Typography>
                    <br />
                    <Typography variant="caption">
                        {
                            "Donnez un titre à votre animation. 90 caractères maximum."
                        }
                    </Typography>
                    <Field
                        name="title"
                        component={RFInputField}
                        label="Titre"
                        type="text"
                        required
                    />
                </Box>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        {
                            "Descriptif de l'animation (nature, partenaires, type de visites : libres ou guidées, etc.)"
                        }
                    </Typography>
                    <br />
                    <Typography variant="caption">
                        {"600 caractères max."}
                    </Typography>

                    <Field
                        name="description"
                        component={RFInputFieldProgress}
                        label="Description"
                        maxLength="600"
                        type="text"
                        value="description"
                        required
                    />
                </Box>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        Photos
                    </Typography>
                    <Typography variant="body1" className="indication">
                        {
                            "Merci de n'envoyer que des fichiers de type image (PNG ou JPG). Le poids maximum par image est de 2.5Mo."
                        }
                    </Typography>
                    <Field
                        name="medias"
                        component={RFUploadField}
                        label="Test"
                    />
                </Box>
                <Box>
                    <Field
                        name="publicAddress"
                        component={RFInputField}
                        label="Lieu d'accueil du public"
                        type="text"
                        className={classes.spacingUp}
                        required
                    />
                    <Field
                        name="publicPostalCode"
                        component={RFInputField}
                        label="Code postal"
                        type="text"
                        required
                    />
                    <Field
                        name="publicCity"
                        component={RFInputField}
                        label="Commune"
                        type="text"
                        required
                    />
                </Box>
                <Typography
                    variant=" "
                    className={[classes.spacingUp, "form__title"].join(" ")}
                >
                    {"Date de l'événement *"}
                </Typography>
                <Box>
                    <Field
                        name="dateEvent"
                        component={RFCheckboxGroup}
                        className="form__checkbox"
                        options={dates}
                        label="Date de l'animation"
                    />
                </Box>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        Si vous demandez aux visiteurs une participation à vos
                        frais d’organisation, indiquez le montant *
                    </Typography>
                    <br />
                    <Typography variant="caption">
                        Attention dans la mesure du possible, votre animation
                        doit être gratuite.
                    </Typography>
                    <Field
                        name="price"
                        component={RFPriceField}
                        label="Prix"
                        type="text"
                    />
                </Box>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        {"Horaires d'accueil *"}
                    </Typography>
                    <Field
                        name="openHour"
                        component={RFInputField}
                        label="Heures d'ouverture et fermeture, horaires des visites..."
                        type="text"
                        required
                    />
                </Box>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        {"Coordonnées *"}
                    </Typography>
                    <br />
                    <Typography variant="caption">
                        Téléphone et/ou mail à communiquer au public
                    </Typography>
                    <Field
                        name="ownerPublicName"
                        component={RFInputField}
                        label="Nom du contact"
                        type="text"
                        required
                    />
                    <Field
                        name="ownerPublicPhone"
                        component={RFInputField}
                        label="Téléphone"
                        type="text"
                        required
                    />
                    <Field
                        name="ownerPublicMail"
                        component={RFInputField}
                        label="Mail"
                        type="email"
                    />
                </Box>
                <Box>
                    <Typography
                        variant=" "
                        className={[classes.spacingUp, "form__title"].join(" ")}
                    >
                        Page web de votre structure à communiquer éventuellement
                        au public (site internet, facebook…) :
                    </Typography>
                    <Field
                        name="website"
                        component={RFInputField}
                        label="Site web"
                        type="text"
                    />
                    <Field
                        name="facebookLink"
                        component={RFInputField}
                        label="Facebook"
                        type="text"
                    />
                </Box>

                <div>
                    {!valid && (
                        <div>
                            <Typography
                                variant="caption"
                                className={"form__submitValidation"}
                            >
                                <WarningIcon />
                                <span>
                                    Veuillez remplir tous les champs obligatoire
                                    pour valider.
                                </span>
                            </Typography>
                        </div>
                    )}
                    <Button
                        variant="contained"
                        type="submit"
                        className="form__button"
                        disabled={!valid}
                        data-testid="goToPage2"
                    >
                        Suivant
                    </Button>
                </div>
            </form>
        </>
    );
};
const mapStateToProps = state => ({
    regions: state.types.regions,
    natureTypes: state.types.natureTypes,
    networkTypes: state.types.networkTypes,
    structureTypes: state.types.structureTypes,
    error: state.formCustom.error,
    loading: state.formCustom.loading,
    created: state.formCustom.created,
    valid: state.formCustom.valid,
    dataLoading: state.types.loading,
    dataError: state.types.error,
});

export default compose(
    withStyles(styles),
    connect(mapStateToProps),
    reduxForm({
        form: "event",
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
        validate,
    })
)(FirstPage);
